import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Form, DatePicker, Select, Card, message, InputNumber, Input, Spin } from 'antd';
import { Row, Col } from 'react-bootstrap';
import { setPickupDetails, setOrderId } from './store/actionCreators';
import { createAlaCarteOrder, stripeCheckout } from '../../data/API';
import { getMyData } from '../../utils/token';
import moment from 'moment';
import dayjs from 'dayjs';
import { loadStripe } from '@stripe/stripe-js';
import './index.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PAYMENT_KEY);

const { TextArea } = Input;

const PickupDetails = (props) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    lockedPickupDetails,
    deliveryDetails,
    boxCount,
    packingSupplies,
    movingSupplies,
    pricing,
    orderId
  } = useSelector(state => state.alaCarte);

  const { isParentPayClicked, setParentPayClicked, customerData, tabref } = props;

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (lockedPickupDetails) {
      const formattedDate = lockedPickupDetails.pickup_date
        ? dayjs(lockedPickupDetails.pickup_date).format('MM-DD-YYYY')
        : '';

      form.setFieldsValue({
        pickup_date: formattedDate,
        pickup_window_1: lockedPickupDetails.pickup_window_1 || '',
        pickup_window_2: lockedPickupDetails.pickup_window_2 || '',
        pickup_address: lockedPickupDetails.pickup_address || '',
        apt_number: lockedPickupDetails.apt_number || null,
        description: lockedPickupDetails.description || ''
      });
      setIsDataLoaded(true);
    }
  }, [lockedPickupDetails, form]);

  useEffect(() => {
    if (tabref?.current) {
      window.scrollTo({
        top: tabref.current.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  }, [tabref]);

  useEffect(() => {
    if (isParentPayClicked) {
      setParentPayClicked(false);
      handlePayment();
    }
  }, [isParentPayClicked]);

  const handlePayment = async () => {
    try {
      if (!lockedPickupDetails || !isDataLoaded) {
        message.error('Loading pickup details, please wait...');
        return;
      }

      const hasItems = boxCount > 0 || packingSupplies.length > 0 || movingSupplies.length > 0;
      if (!hasItems) {
        message.error('Please select at least one item (boxes, packing supplies, or moving supplies) to proceed with checkout');
        return;
      }

      setIsFormSubmitted(true);

      // Prepare order data
      const userData = getMyData();

      const orderData = {
        customer_id: customerData?.id,
        box_count: boxCount,
        packing_supplies: packingSupplies.map(item => ({
          product_id: item.id,
          quantity: item.quantity
        })),
        moving_supplies: movingSupplies.map(item => ({
          product_id: item.id,
          quantity: item.quantity
        })),
        delivery_details: {
          delivery_date: deliveryDetails.delivery_date,
          delivery_window_1_id: deliveryDetails.delivery_window_1_id,
          delivery_window_2_id: deliveryDetails.delivery_window_2_id,
          delivery_address: deliveryDetails.delivery_address,
          extra_work_id: deliveryDetails.extra_work_id || null,
          apt_number: deliveryDetails.apt_number || null,
          description: deliveryDetails.description || ''
        },
        // Use locked pickup details from rental order
        pickup_details: {
          pickup_date: lockedPickupDetails.pickup_date,
          pickup_window_1_id: lockedPickupDetails.pickup_window_1_id,
          pickup_window_2_id: lockedPickupDetails.pickup_window_2_id,
          pickup_window_1: lockedPickupDetails.pickup_window_1,
          pickup_window_2: lockedPickupDetails.pickup_window_2,
          pickup_address: lockedPickupDetails.pickup_address,
          extra_work_id: lockedPickupDetails.extra_work_id || null,
          apt_number: lockedPickupDetails.apt_number || null,
          description: lockedPickupDetails.description || ''
        },
        total_amount: pricing.total,
        transportation_fee: pricing.transportationFee,
        tax: pricing.tax
      };

      // If order exists, include it in the payload for UPDATE
      if (orderId) {
        orderData.order_id = orderId;
        console.log('Updating existing order:', orderId);
      } else {
        console.log('Creating new order');
      }

      // Always call API to create or update order
      const orderResponse = await createAlaCarteOrder(orderData);
      console.log('Order response:', orderResponse);

      // Get order ID from response
      const currentOrderId = orderResponse?.data?.data?.order_id || orderResponse?.data?.order_id;

      if (!currentOrderId) {
        message.error('Failed to create/update order');
        setIsFormSubmitted(false);
        return;
      }

      // Save order ID to Redux
      if (!orderId) {
        dispatch(setOrderId(currentOrderId));
        console.log('Created order ID:', currentOrderId);
      } else {
        console.log('Updated order ID:', currentOrderId);
      }

      // Create Stripe checkout session
      const userEmail = userData?.email;
      const stripeSession = await stripeCheckout({
        payment: { billing_details: { email: userEmail } },
        session: currentOrderId,
        order_type: 'packages',
        amount: pricing.total,
        discount: 0,
        driver_tip: 0
      });

      console.log('Stripe session response:', stripeSession);

      if (stripeSession?.data?.sessionId) {
        console.log('Redirecting to Stripe checkout with sessionId:', stripeSession.data.sessionId);
        const stripe = await stripePromise;
        const result = await stripe.redirectToCheckout({
          sessionId: stripeSession.data.sessionId
        });

        if (result.error) {
          console.error('Stripe redirect error:', result.error);
          message.error('Payment redirect failed. Please try again.');
          setIsFormSubmitted(false);
        }
      } else {
        console.error('No sessionId in response:', stripeSession);
        message.error('Failed to create checkout session');
        setIsFormSubmitted(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      message.error('Failed to process payment. Please try again.');
      setIsFormSubmitted(false);
    }
  };

  // Show loading state if pickup details haven't loaded yet
  if (!lockedPickupDetails || !isDataLoaded) {
    return (
      <div className="my-4 text-center" style={{ padding: '50px' }}>
        <Spin tip="Loading pickup details from your rental order..." />
      </div>
    );
  }

  return (
    <div>
      <div className="steps bg-white-100" id="steps">
        <h2 className="shopping-cart-header text-white text-center py-2 fs-20 steps-title">
          Pickup Details
        </h2>
        <Card bordered={false} className="w-100 px-3 pt-3 pb-4">
          <div className="pickup-info-note">
            <p>
              We&apos;ve automatically set your pickup date based on the pickup date of the rental order this is linked to.
              If you need to change the pickup date or extend your rental duration, you can do so from the customer portal.
            </p>
          </div>
          <Spin spinning={isFormSubmitted} tip="processing...">
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              style={{ position: 'relative', zIndex: 0 }}
            >
              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="pickup_date"
                    label="Pickup Date"
                  >
                    <Input
                      size="large"
                      readOnly={true}
                      style={{ cursor: 'not-allowed' }}
                    />
                  </Form.Item>
                </Col>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="pickup_window_1"
                    label="Pickup Window 1"
                  >
                    <Input
                      size="large"
                      readOnly={true}
                      style={{ cursor: 'not-allowed' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="pickup_window_2"
                    label="Pickup Window 2"
                  >
                    <Input
                      size="large"
                      readOnly={true}
                      style={{ cursor: 'not-allowed' }}
                    />
                  </Form.Item>
                </Col>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="pickup_address"
                    label="Pickup Address"
                  >
                    <Input
                      size="large"
                      readOnly={true}
                      style={{ cursor: 'not-allowed' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item name="apt_number" label="Apt.">
                    <InputNumber
                      className="w-100"
                      placeholder="Apt."
                      size="large"
                      controls={false}
                      readOnly={true}
                      style={{ cursor: 'not-allowed' }}
                    />
                  </Form.Item>
                </Col>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="description"
                    label="Additional Notes"
                  >
                    <TextArea
                      autoSize={{
                        minRows: 3,
                        maxRows: 5
                      }}
                      readOnly={true}
                      style={{ cursor: 'not-allowed' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Card>
      </div>
    </div>
  );
};

PickupDetails.propTypes = {
  isParentPayClicked: PropTypes.bool,
  setParentPayClicked: PropTypes.func,
  customerData: PropTypes.object,
  tabref: PropTypes.object,
};

export default PickupDetails;
